import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';
import { Dialog } from '@/common/components/Dialog/Dialog';
import { Icon } from '@/common/components/Icons/Icon';
import { Translate } from '@/common/components/Translate/Translate';
import { determineKinships } from '@/common/constants/kinship';
import { AddMemberCardForm } from '@/common/containers/Dashboard/Insurance/InsuranceAddMembersPage/components/AddMemberCardForm/AddMemberCardForm';
import {
  TAddFamilyMembersFormValues,
  addFamilyMembersFormSchema,
} from '@/common/containers/Dashboard/Insurance/InsuranceAddMembersPage/components/AddMemberCardForm/AddMemberCardForm.schema';
import {
  convertFieldsToMenhir,
  getFirstErrorIndex,
  shouldShowWarningModal,
} from '@/common/containers/Dashboard/Insurance/InsuranceAddMembersPage/components/FamilyMembersInfoStep/FamilyMembersInfoStep.utils';
import { useInsuranceContext } from '@/common/contexts/insurance/InsuranceContext';
import { transformMenhirPlanSimulationPerson } from '@/common/integrations/menhir/employee/insurance/insurance.dto';
import {
  useCreatePlanEligibility,
  useCreatePlanSimulation,
} from '@/common/integrations/menhir/employee/insurance/insurance.hooks';
import {
  TMemberEligibility,
  TMemberSimulation,
} from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { FlowLayout } from '@/common/layouts/FlowLayout/FlowLayout';
import { useFlowLayout } from '@/common/layouts/FlowLayout/FlowLayout.context';
import { parseDate } from '@/common/utils/date';
import { notify } from '@/common/utils/toast/toast';
import { ControllerContext } from '@/web/components/Fields/ControllerContext/ControllerContext';
import { useMarket } from '@/web/hooks/useMarket';
import { genders } from '@coverflex-tech/hypernova-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocalSearchParams } from 'expo-router';

export const FamilyMembersInfoStep = ({
  persons,
  policyId,
  onRemove,
}: TFamilyMembersInfoStepProps) => {
  const { t } = useTranslation();
  const { setSimulation } = useInsuranceContext();
  const { mutateAsync, isLoading } = useCreatePlanEligibility();
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [cardRefs, setCardRefs] = useState<React.RefObject<View | null>[]>([]);
  const [firstError, setFirstError] = useState<number | null>(null);
  const { slug } = useMarket();
  const kinshipList = useMemo(() => determineKinships(slug), [slug]);
  const { goNext } = useFlowLayout();
  const params = useLocalSearchParams<{ policyId: string }>();
  const { mutateAsync: requestSimulation } = useCreatePlanSimulation();

  const kinshipToString = kinshipList.map((kinship) => ({
    ...kinship,
    label: t(kinship.label),
  }));
  const genderToString = genders.map((gender) => ({
    ...gender,
    label: t(gender.label),
  }));

  const methods = useForm<TAddFamilyMembersFormValues>({
    mode: 'onTouched',
    defaultValues: {
      members: persons
        .filter((person) => person.selected)
        .map((person) => ({
          type:
            kinshipToString.find((k) => k.value === person.type) ?? undefined,
          fullName: person.fullName ?? '',
          shortName: person.shortName ?? '',
          taxId: person.taxNumber?.value ?? '',
          birthdate: person.birthdate ? parseDate(person.birthdate) : undefined,
          gender:
            genderToString.find((gender) => person.gender === gender.value) ??
            undefined,
          isUserAdded: person.isUserAdded ?? false,
          selected: person.selected ?? false,
          memberId: person.id,
          editableFields: person.editableFields,
        })),
    },
    resolver: zodResolver(addFamilyMembersFormSchema),
  });

  const { fields, remove } = useFieldArray({
    control: methods.control,
    name: 'members',
  });

  useEffect(() => {
    if (firstError !== null && Platform.OS === 'web') {
      //@ts-ignore
      cardRefs[firstError]?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [cardRefs, firstError]);

  const removeFromList = (
    field: TAddFamilyMembersFormValues['members'][number],
    index: number
  ) => {
    remove(index);
    onRemove(field);
  };

  const requestPersonsEligibilty = async (
    form: TAddFamilyMembersFormValues
  ) => {
    try {
      const response = await mutateAsync({
        policyId,
        persons: convertFieldsToMenhir(form.members, slug),
      });
      methods.reset({
        members: form.members.map((member, index) => ({
          ...member,
          error: response[index].error,
        })),
      });

      setCardRefs(response.map(() => React.createRef()));
      setFirstError(getFirstErrorIndex(response));

      const errorIndex = shouldShowWarningModal(response);
      if (errorIndex !== null) {
        setFirstError(errorIndex);
        setShowWarningModal(true);
      } else if (!response.some((member: TMemberEligibility) => member.error)) {
        onConfirmFamilyMembers(form.members);
        goNext();
      }
    } catch (error) {
      notify({
        message: t('errors.api.generic'),
        type: 'error',
      });
    }
  };

  const getWarningMessage = (
    persons: TAddFamilyMembersFormValues['members']
  ) => {
    const membersWithIssues = persons.filter(
      (member) => member.error === 'already_exists'
    );

    return (
      <>
        {membersWithIssues.map((member) => (
          <Translate
            key={member.taxId}
            i18nKey="dashboard.insurance_detail.add_family_members.modal.warning.message"
            values={{ short_name: member.shortName, tax_id: member.taxId }}
          />
        ))}
      </>
    );
  };

  const onConfirmFamilyMembers = async (
    fields: TAddFamilyMembersFormValues['members']
  ) => {
    try {
      const originalPersons = persons.map(transformMenhirPlanSimulationPerson);
      const formPersons = convertFieldsToMenhir(fields, slug);
      const finalPersons = originalPersons.map((person) => {
        const formPerson = formPersons.find(
          (formPerson) => formPerson.id === person.id
        );
        return formPerson ?? person;
      });

      const response = await requestSimulation({
        policyId: params.policyId as string,
        persons: finalPersons,
      });

      setSimulation(response);
    } catch (error) {
      notify({
        message: t('errors.api.generic'),
        type: 'error',
      });
    }
  };

  return (
    <FlowLayout.Content
      primaryAction={{
        onPress: () => {
          methods.handleSubmit((data) => {
            requestPersonsEligibilty(data);
          })();
        },
        loading: isLoading,
      }}
    >
      <FormProvider {...methods}>
        {showWarningModal && (
          <ControllerContext<TAddFamilyMembersFormValues>
            render={({ getValues }) => (
              <Dialog
                iconBadgeColor="accentSecondary20"
                icon={<Icon icon="family-pants" />}
                title={t(
                  'dashboard.insurance_detail.add_family_members.modal.warning.title'
                )}
                message={getWarningMessage(getValues().members)}
                primaryAction={{
                  children: t(
                    'dashboard.insurance_detail.add_family_members.modal.warning.continue.btn'
                  ),
                  onPress: (close) => {
                    onConfirmFamilyMembers(getValues().members);
                    goNext();
                    close();
                  },
                }}
                secondaryAction={{
                  children: t(
                    'dashboard.insurance_detail.add_family_members.modal.warning.review.btn'
                  ),
                  onPress: (close) => {
                    close();
                  },
                }}
                onClose={() => setShowWarningModal(false)}
              />
            )}
          />
        )}
        {fields.map((field, index) => {
          return (
            <AddMemberCardForm
              key={field.id}
              memberIndex={index}
              member={field}
              disabled={fields.length < 2}
              onRemove={() => removeFromList(field, index)}
              cardRef={cardRefs[index]}
            />
          );
        })}
      </FormProvider>
    </FlowLayout.Content>
  );
};

export type TFamilyMembersInfoStepProps = {
  persons: TMemberSimulation[];
  policyId: string;
  onRemove: (member: TAddFamilyMembersFormValues['members'][number]) => void;
};
